import { useEffect } from 'react';
import { useMatch, useNavigate } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { listRooms } from './db/rooms';
import { useSearchStore } from './search/searchStore';
import { isBlocked, isMod, isTextField } from './ui/keys';

/**
 * The shortcuts that work anywhere:
 *
 *   Cmd/Ctrl + K    search — this room if you're on a board, everything if not
 *   1 – 9           jump to the nth room in the rail
 *
 * The rest are owned by whatever they act on: the panel's arrows and Escape
 * are in `item/useItemNav.ts`, and the board's new-item and delete-selection
 * keys are in `board/useBoardKeys.ts`. A shortcut belongs with the thing it
 * does, or it ends up reaching across the app to find it.
 *
 * Lives beside `App.tsx` because App is the only caller and these are App's
 * bindings, not a shared primitive.
 */
export function useAppKeys(): void {
  const navigate = useNavigate();
  const rooms = useLiveQuery(listRooms, []);
  const openSearch = useSearchStore((state) => state.openSearch);

  const onBoard = useMatch('/room/:roomId/*') !== null;

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.defaultPrevented || isBlocked()) return;

      if (isMod(event) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        // Off a board, "this room" means nothing; the overlay would only
        // correct it a render later.
        openSearch(onBoard ? 'room' : 'everywhere');
        return;
      }

      // Single-key from here down. Never while there's text being typed, and
      // never with a modifier — Cmd+1 and Ctrl+1 belong to the window.
      if (isTextField(event.target) || isMod(event) || event.altKey) return;

      if (event.key >= '1' && event.key <= '9') {
        const room = (rooms ?? [])[Number(event.key) - 1];
        if (room === undefined) return;
        event.preventDefault();
        void navigate(`/room/${room.id}`);
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [rooms, navigate, openSearch, onBoard]);
}
